<template>
  <BasePage :page-title="'Room ' + currentRoom?.roomName" back-button show-menu>
    <!-- Loading State (including PWA background recovery) -->
    <LoadingSpinner v-if="loading || isRecovering" />

    <!-- Main Content -->
    <div v-else-if="currentRoom && isReady" class="d-flex flex-column" style="min-height: calc(100vh - 300px)">
      <!-- Room Information Display -->
      <Card title="Room Information" show-status-bar margin="mb-6">
          <!-- Room Information -->
          <table class="table table-vcenter"
                 v-if="currentRoom.ready === 1 && currentRoom.blocked === 0 && !currentRoom.distributed">
            <tbody>
            <tr>
              <td>{{ __DICT__.building }}</td>
              <td>{{ currentRoom.buildingName }}</td>
            </tr>
            <tr>
              <td>{{ __DICT__.room }}</td>
              <td>{{ currentRoom.roomName }}</td>
            </tr>
            <tr>
              <td>{{ __DICT__.numberOfKeys }}</td>
              <td>{{ currentRoom.numberOfKeys || "Not specified" }}</td>
            </tr>
            <tr>
              <td>{{ __DICT__.extraKeys }}</td>
              <td>{{ currentRoom.extraKeys || "Not specified" }}</td>
            </tr>
            <tr>
              <td>{{ __DICT__.keyDistribution.linenConfiguration }}</td>
              <td>{{ currentRoom.linenConfig || "Not specified" }}</td>
            </tr>
            <tr v-if="currentRoom.distributionInfo">
              <td>{{ __DICT__.keyDistribution.partialDistribution }}</td>
              <td>
                <div v-for="(info, index) in parsedDistributionInfo" :key="index">
                  {{ info.numberOfKeys }} {{ __DICT__.keyDistribution.keysTo }} {{ info.tenant }}
                  <small class="text-muted d-block">{{ formatDate(info.timestamp) }}</small>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
          <div v-else-if="currentRoom.distributed && currentRoom.distributedTo" class="alert alert-danger">
            {{ __DICT__.keyDistribution.keysAlreadyDistributed }}
            <router-link class="link-azure" :to="`/passenger/${currentRoom.distributedTo?.split(' - ')[1] || ''}`">
              {{ currentRoom.distributedTo }}
            </router-link>

          </div>

          <div v-if="currentRoom.distributed" class="alert alert-danger mt-2">
            <table class="table mt-2">
              <tbody>
              <tr>
                <td>{{ __DICT__.keyDistribution.keysDistributed }}</td>
                <td>{{ currentRoom.numberOfKeys || "Not specified" }}</td>
              </tr>
              <tr>
                <td>{{ __DICT__.extraKeys }}</td>
                <td>{{ currentRoom.extraKeys || "Not specified" }}</td>
              </tr>
              <tr>
                <td>{{ __DICT__.keyDistribution.linenConfiguration }}</td>
                <td>{{ currentRoom.linenConfig || "Not specified" }}</td>
              </tr>
              </tbody>
            </table>
          </div>

          <Card v-if="currentRoom.distributed" title="Keys Return" show-status-bar margin="mt-3">
            <div v-if="currentRoom.returned">
              {{ __DICT__.keyDistribution.keysReturned }}
            </div>
            <div v-if="!currentRoom.returned">
              <div class="mb-3">
                <label class="form-label">{{ __DICT__.numberOfKeys }}</label>
                <Select v-model="keysToReturn" :options="returnableKeyOptions" />
              </div>
              <div class="mb-3">
                <label class="form-label">{{ __DICT__.keyDistribution.returnedBy }}</label>
                <Select v-model="returnedBy" :options="returnedByOptions" />
              </div>
              <div class="mb-3">
                <label class="form-label">{{ __DICT__.keyDistribution.extraKeysReturned }}</label>
                <Select v-model="extraKeysReturned" :options="extraKeysOptions" />
              </div>
              <div class="mb-3">
                <label class="form-label">{{ __DICT__.notes }}</label>
                <textarea v-model="returnedNotes" class="form-control">

                </textarea>
              </div>
            </div>
          </Card>

          <div v-else-if="currentRoom.blocked" class="alert alert-important alert-danger">
            {{ __DICT__.keyDistribution.roomBlocked }}
          </div>
          <div v-else-if="currentRoom.ready !== 1" class="alert alert-important alert-danger">
            {{ __DICT__.keyDistribution.roomNotReadyForDist }}
          </div>
          <!-- Reps see the room details before the keys are ready too (decided 2026-10-02). -->
          <table v-if="!currentRoom.distributed && currentRoom.ready !== 1" class="table table-vcenter mt-2">
            <tbody>
            <tr v-if="currentRoom.estimatedCollectionTime">
              <td>{{ __DICT__.keyDistribution.estimatedCollection }}</td>
              <td>{{ formatEct(currentRoom.estimatedCollectionTime, currentRoom.estimatedCollectionDate) }}</td>
            </tr>
            <tr>
              <td>{{ __DICT__.numberOfKeys }}</td>
              <td>{{ currentRoom.numberOfKeys || "Not specified" }}</td>
            </tr>
            <tr>
              <td>{{ __DICT__.extraKeys }}</td>
              <td>{{ currentRoom.extraKeys || "Not specified" }}</td>
            </tr>
            <tr>
              <td>{{ __DICT__.keyDistribution.linenConfiguration }}</td>
              <td>{{ currentRoom.linenConfig || "Not specified" }}</td>
            </tr>
            </tbody>
          </table>
      </Card>

      <!-- Key Distribution Form -->
      <Card v-if="currentRoom.ready === 1 && currentRoom.blocked === 0 && !currentRoom.distributed" title="Distribute Keys" show-status-bar margin="">
          <div class="mb-3">
            <label class="form-label">{{ __DICT__.passenger }} {{ __DICT__.name }}</label>
            <div v-if="tenantName">{{ tenantName }}</div>
            <div v-else class="text-muted">{{ __DICT__.keyDistribution.noPassengerInfo }}</div>
          </div>
          <div class="mb-3" v-if="currentRoom.numberOfKeys">
            <label class="form-label">{{ __DICT__.numberOfKeys }}</label>
            {{ currentRoom.numberOfKeys }}
          </div>
      </Card>

      <div class="mt-2 mb-6 alert alert-important alert-info" role="alert"
           v-if="!scannedBarcode && !currentRoom.distributed && currentRoom.ready === 1 && currentRoom.blocked === 0">
        {{ __DICT__.keyDistribution.scanPromptBefore }} {{ __DICT__.passenger }} {{ __DICT__.keyDistribution.scanPromptAfter }}
      </div>

      <!-- Scanner Component -->
      <QrScanner
          v-if="showingScanner"
          @code-scanned="handleBarcodeScan"
          @invalid-code="handleInvalidCode"
      />

      <!-- Action Buttons -->
      <div class="fixed-bottom fixed-bottom-bar p-3">
        <!-- Room Selected State Buttons -->
        <template v-if="!showingScanner">
          <!-- KD-05: reps with the right level mark keys ready here -->
          <SlideConfirmButton
              v-if="showMarkReady"
              :title="keyStatusOf(currentRoom) === 'delayed' ? __DICT__.keyDistribution.markReadyApology : __DICT__.keyDistribution.markReadySlide"
              @complete="markKeysReady"
          />
          <SlideConfirmButton
              v-if="canDistributeKeys"
              title="Slide to distribute keys"
              @complete="handleDistributionSubmit"
          />
          <SlideConfirmButton
              v-if="canReturnKeys"
              title="Slide to return keys"
              @complete="handleReturnSubmit"
          />
          <button
              v-if="!scannedBarcode && currentRoom.ready === 1 && currentRoom.blocked === 0 && !currentRoom.distributed"
              class="btn btn-primary w-100"
              @click="startScanner"
          >
            {{ __DICT__.keyDistribution.scanBarcodeToVerify }}
          </button>
          <button
              class="btn btn-secondary w-100 mt-2"
              @click="navigateBack"
          >
            {{ __DICT__.keyDistribution.backToRoomList }}
          </button>
        </template>

        <!-- Scanner Active State Button -->
        <template v-if="showingScanner">
          <button class="btn btn-secondary w-100" @click="closeScanner">
            {{ __DICT__.cancel }}
          </button>
        </template>
      </div>
    </div>

    <!-- Room Not Found State -->
    <div v-else class="d-flex flex-column align-items-center justify-content-center p-4" style="min-height: calc(100vh - 300px)">
      <div class="alert alert-danger w-100">
        <h4 class="alert-title">{{ __DICT__.keyDistribution.roomNotFound }}</h4>
        <div class="text-muted">{{ __DICT__.keyDistribution.roomLoadError }}</div>
      </div>
      <button class="btn btn-secondary mt-3" @click="navigateBack">
        {{ __DICT__.keyDistribution.backToRoomList }}
      </button>
    </div>

    <!-- Success Modal -->
    <div class="modal modal-blur fade" :class="{ 'show': showSuccessModal }" tabindex="-1" style="display: block;"
         v-if="showSuccessModal">
      <div class="modal-dialog modal-sm modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-body">
            <div class="modal-title text-success">{{ __DICT__.success }}</div>
            <div>{{ successMessage }}</div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-success w-100" @click="acknowledgeSuccess">
              {{ __DICT__.ok }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Error Modal -->
    <div class="modal modal-blur fade" :class="{ 'show': showErrorModal }" tabindex="-1" style="display: block;"
         v-if="showErrorModal">
      <div class="modal-dialog modal-sm modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-body">
            <div class="modal-title text-danger">{{ __DICT__.error }}</div>
            <div>{{ error?.message || 'An error occurred' }}</div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-danger w-100" @click="acknowledgeError">
              {{ __DICT__.ok }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Backdrop -->
    <div class="modal-backdrop fade" :class="{ 'show': showSuccessModal || showErrorModal }"
         v-if="showSuccessModal || showErrorModal"></div>
  </BasePage>
</template>

<script setup>
import {ref, computed, onMounted} from "vue";
import {useWeekStore} from '@/stores/week.js';
import {api} from "@/services/api.js";
import {getLocation} from '@/services/location.js';
import BasePage from "@/components/BasePage.vue";
import { Card, LoadingSpinner, Select } from "@/components/atomic";
import QrScanner from "@/components/QrScanner.vue";
import {qrRequestCode, qrRequestFields} from "@/utils/qrVerify.js";
import {recallScan} from "@/services/qrScan.js";
import SlideConfirmButton from "@/components/SlideConfirmButton.vue";
import {useRoute, useRouter} from "vue-router";
import {useEnsureWeekData} from "@/composables/useEnsureWeekData.js";
import {passengerIndex} from "@/services/PassengerIndex.js";
import {useHaptics} from "@/composables/useHaptics.js";
import {useScanRedeem} from "@/composables/useScanRedeem.js";
import {keyStatusOf, formatEct} from "@/utils/keyStatus.js";

// State Management
const weekStore = useWeekStore();
const { successFeedback, errorFeedback } = useHaptics();

// Ensure week data is available (handles PWA background recovery)
const { isRecovering, isReady } = useEnsureWeekData();
const loading = ref(true);
const currentRoom = ref(null);
const scannedBarcode = ref(null);
const tenantName = ref('');
const keysToDistribute = ref(1);
const keysToReturn = ref(1);
const returnedBy = ref('');
const extraKeysReturned = ref('');
const returnedNotes = ref('');
const showingScanner = ref(false);
const {processing, error, showSuccessModal, showErrorModal, redeem} = useScanRedeem({
  refresh: async () => {
    await loadRoomData();
    await loadAllowedPassengers();
  }
});
const successMessage = ref('');
const route = useRoute();
const router = useRouter();
const allowedPassengers = ref([]);
const canMarkReady = ref(false);

// KD-05: the slide shows for a permitted rep on a room that is not ready yet.
const showMarkReady = computed(() => {
  const room = currentRoom.value;
  return canMarkReady.value && !!room && !room.distributed && room.blocked !== 1 &&
      (room.numberOfKeys || 0) > 0 &&
      ['not_ready', 'ect_confirmed', 'delayed'].includes(keyStatusOf(room));
});

const markKeysReady = async () => {
  processing.value = true;
  try {
    await api.post('/api/keyDist/status', {weekId: weekStore.weekId, ids: [currentRoom.value.id], status: 'ready'});
    successFeedback();
    successMessage.value = __DICT__.keyDistribution.markedReady;
    showSuccessModal.value = true;
    await loadRoomData();
  } catch (err) {
    errorFeedback();
    error.value = err;
    showErrorModal.value = true;
  } finally {
    processing.value = false;
  }
};


const canReturnKeys = computed(() => {
  return currentRoom.value &&
      currentRoom.value.distributed &&
      returnedBy.value !== '' && extraKeysReturned.value !== '';
});

const canDistributeKeys = computed(() => {
  return currentRoom.value &&
      !currentRoom.value.distributed &&
      currentRoom.value.ready === 1 &&
      currentRoom.value.blocked === 0 &&
      currentRoom.value.numberOfKeys > 0 &&
      tenantName.value.trim() !== '' &&
      scannedBarcode.value;
});

const getReturnableKeyOptions = () => {
  if (!currentRoom.value) return [1];
  const totalKeys = currentRoom.value.numberOfKeys || 1;
  return Array.from({length: totalKeys}, (_, i) => i + 1);
};

const returnableKeyOptions = computed(() => {
  return getReturnableKeyOptions().map(n => ({
    value: n,
    label: `${n} key${n > 1 ? 's' : ''}`
  }));
});

const returnedByOptions = computed(() => [
  { value: '', label: '-- Select --' },
  { value: 'not_returned_by_person', label: 'Not returned by a person' },
  ...allowedPassengers.value.map(p => ({
    value: p.displayName,
    label: p.displayName
  }))
]);

const extraKeysOptions = [
  { value: '', label: '-- Select --' },
  { value: '1', label: 'Yes' },
  { value: '0', label: 'No' }
];

const parsedDistributionInfo = computed(() => {
  if (!currentRoom.value || !currentRoom.value.distributionInfo) return [];
  try {
    return JSON.parse(currentRoom.value.distributionInfo);
  } catch (e) {
    console.error('Error parsing distribution info:', e);
    return [];
  }
});


const loadAllowedPassengers = async () => {
  if (!currentRoom.value || !currentRoom.value.allowedBarcodes) {
    allowedPassengers.value = [];
    return;
  }

  const barcodes = currentRoom.value.allowedBarcodes.split(',');
  const passengersList = [];

  for (const barcode of barcodes) {
    const passenger = await findPassengerByBarcode(barcode);
    if (passenger && passenger.name) {
      passengersList.push({
        name: passenger.name,
        bookingRef: passenger.bookingRef,
        displayName: `${passenger.name} - ${passenger.bookingRef}`
      });
    }
  }

  allowedPassengers.value = passengersList;
};

// Methods
const generateGuid = () => {
  const s4 = () => Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
};

const formatDate = (timestamp) => {
  if (!timestamp) return '';
  return new Date(timestamp).toLocaleString();
};

const getAvailableKeyOptions = () => {
  if (!currentRoom.value) return [1];

  const totalKeys = currentRoom.value.numberOfKeys || 1;
  const distributedKeys = parsedDistributionInfo.value.reduce((sum, info) => sum + (info.numberOfKeys || 0), 0);
  const remainingKeys = totalKeys - distributedKeys;

  // Create array from 1 to remainingKeys
  return Array.from({length: remainingKeys}, (_, i) => i + 1);
};

const startScanner = () => {
  showingScanner.value = true;
};

const closeScanner = () => {
  showingScanner.value = false;
};

const findPassengerByBarcode = async (barcode) => {
  const passengerData = await weekStore.weekData;
  const parts = barcode.split('-');
  const bookingRef = parts.length >= 2 ? `${parts[0]}-${parts[1]}` : barcode;

  // Use O(1) indexed lookup instead of O(n) find
  const passenger = passengerIndex.getByReference(bookingRef);
  return {
    name: passenger?.passengerName || 'Unknown Passenger',
    bookingRef: bookingRef
  };
};

const navigateBack = () => {
  router.back()
};

// The scan behind scannedBarcode: { raw, token, verification }. Arrives as the
// scanner's second argument here, or - when KeyDistribution scanned and then
// navigated (/keyDist/room/:roomId/scan/:barcode) - from the remembered scan.
// Null for a manual selection.
let currentScan = null

const handleBarcodeScan = async (barcode, scan = null) => {
  if (processing.value) return;
  processing.value = true;
  error.value = null;
  currentScan = scan;

  try {
    // Verify the barcode is allowed for this room
    if (currentRoom.value.allowedBarcodes) {
      const allowedBarcodes = currentRoom.value.allowedBarcodes.split(',');
      if (!allowedBarcodes.includes(barcode)) {
        throw new Error('This barcode is not authorized for this room');
      }

      scannedBarcode.value = barcode;
      showingScanner.value = false;

      // Get passenger name
      const passenger = await findPassengerByBarcode(barcode);
      tenantName.value = `${passenger.name} - ${passenger.bookingRef}`;
    } else {
      throw new Error('No allowed barcodes for this room');
    }
  } catch (e) {
    error.value = e;
    showErrorModal.value = true;
  } finally {
    processing.value = false;
  }
};

const handleInvalidCode = () => {
  error.value = {message: 'Invalid barcode format'};
  showErrorModal.value = true;
  errorFeedback();
};

const handleDistributionSubmit = async () => {
  if (processing.value || !canDistributeKeys.value) return;

  processing.value = true;
  error.value = null;

  // Guard against null repObj (can happen when PWA resumes from background)
  if (!weekStore.repObj) {
    error.value = { message: 'Session data not available. Please go back and try again.' };
    showErrorModal.value = true;
    processing.value = false;
    return;
  }

  try {
    const {latitude, longitude} = await getLocation();
    const location = navigator.geolocation ? `${latitude},${longitude}` : '';
    let lat = "N/A";
    let lon = "N/A";
    try {
      lat = latitude.toString();
      lon = longitude.toString();
    } catch (e) {
      console.error("Location services not enabled")
    }
    const payload = {
      weekId: weekStore.systemId,
      roomId: currentRoom.value.roomId,
      barcode: qrRequestCode(currentScan, scannedBarcode.value),
      ...qrRequestFields(currentScan, scannedBarcode.value),
      handsetTimestamp: Date.now(),
      repId: weekStore.repObj.repId,
      repName: weekStore.repObj.name,
      idempotentId: generateGuid(),
      tenant: tenantName.value,
      numberOfKeys: keysToDistribute.value,
      location: location,
      lat: lat,
      lon: lon,
    };

    // Worded before the refresh, which reloads the room.
    const handedOver = keysToDistribute.value;
    successMessage.value = handedOver < (currentRoom.value.numberOfKeys || 1)
      ? `${handedOver} key(s) successfully distributed to ${tenantName.value}`
      : `All keys successfully distributed to ${tenantName.value}`;
    await redeem('/api/v3/scan/room-key', payload);
  } catch (e) {
    error.value = e;
    showErrorModal.value = true;
    errorFeedback();
  } finally {
    processing.value = false;
  }
};

const handleReturnSubmit = async () => {
  if (processing.value || !canReturnKeys.value) return;

  // Guard against null repObj (can happen when PWA resumes from background)
  if (!weekStore.repObj) {
    error.value = { message: 'Session data not available. Please go back and try again.' };
    showErrorModal.value = true;
    return;
  }

  const {latitude, longitude} = await getLocation();
  processing.value = true;
  error.value = null;
  let lat = "N/A";
  let lon = "N/A";
  try {
    lat = latitude.toString();
    lon = longitude.toString();
  } catch (e) {
    console.error("Location services not enabled")
  }
  try {
    const payload = {
      weekId: weekStore.systemId,
      roomId: currentRoom.value.roomId,
      handsetTimestamp: Date.now(),
      repId: weekStore.repObj.repId,
      repName: weekStore.repObj.name,
      idempotentId: generateGuid(),
      numberOfKeys: keysToReturn.value,
      returnedBy: returnedBy.value,
      returnedNotes: returnedNotes.value,
      extraKeysReturned: extraKeysReturned.value,
      lat: lat,
      lon: lon,
    };

    await api.post('/api/keyDist/return', payload);
    await loadRoomData();

    successMessage.value = `${keysToReturn.value} key(s) successfully returned`;
    showSuccessModal.value = true;
    successFeedback();
  } catch (e) {
    error.value = e;
    showErrorModal.value = true;
    errorFeedback();
  } finally {
    processing.value = false;
  }
};

const acknowledgeSuccess = () => {
  showSuccessModal.value = false;

  if (
      (currentRoom.value.distributed) ||
      (successMessage.value.includes('All keys'))
  ) {
    navigateBack();
  }
};

const acknowledgeError = () => {
  showErrorModal.value = false;
  error.value = null;
};

const loadRoomData = async () => {
  try {
    const roomId = route.params.roomId;
    const weekId = weekStore.systemId;
    const roomResponse = await api.get(`/api/keyDist/room/${roomId}/week/${weekId}`);
    if (roomResponse.roomId) {
      currentRoom.value = roomResponse;
      await loadAllowedPassengers();
      if (route.params.barcode) {
        scannedBarcode.value = route.params.barcode;
        const routedScan = route.params.scanType === 'scan' ? recallScan(route.params.barcode) : null;
        await handleBarcodeScan(scannedBarcode.value, routedScan);
      }
      // Set initial values
      keysToDistribute.value = currentRoom.value.numberOfKeys || 1;
    } else {
      error.value = {message: roomResponse.error || 'Failed to load room data'};
      showErrorModal.value = true;
    }

  } catch (err) {
    error.value = err;
    showErrorModal.value = true;
  } finally {
    loading.value = false;
  }
};

// Initial Data Load
onMounted(async () => {
  await loadRoomData();
  try {
    const permission = await api.get(`/api/keyDist/canMarkReady/${weekStore.weekId}`);
    canMarkReady.value = !!permission.allowed;
  } catch {
    canMarkReady.value = false;
  }
});
</script>

<style scoped>
/* Ensure badges have white text */
.badge {
  color: white !important;
}

/* Improve modal appearance */
.modal-content {
  border: none;
  box-shadow: var(--tblr-box-shadow-lg);
}

/* Style for distributed keys status */
.alert-danger {
  margin-bottom: 0;
}

/* Card styling */
.card {
  box-shadow: var(--tblr-box-shadow-sm);
  border: none;
}

.card-header {
  background-color: var(--tblr-bg-surface);
  border-bottom: 1px solid var(--tblr-border-color);
  position: relative;
}

.card-status-top {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 3px;
  border-radius: 3px 3px 0 0;
}
</style>
